/* istanbul ignore file -- @preserve */

import { TypePolicies } from '@apollo/client';

export const typePolicies: TypePolicies = {
  // `primary` on ContactDepot is scoped to the contact the depot is attached to
  // (a contact is the primary for a depot — the depot is not "primary" on its
  // own). Normalizing by `id` would make every contact share one ContactDepot
  // entity, so the last-written `primary` would leak across contacts and the
  // Edit Contact modal would pre-check the wrong depots. Keep ContactDepot
  // embedded in its parent contact instead.
  ContactDepot: {
    keyFields: false,
  },
  Appointment: {
    fields: {
      history: {
        keyArgs: ['filters'],
        // `page` is deliberately not a key arg: every page of a given `filters`
        // set merges into one cached field so that "load more" can append to what
        // is already there. That makes the merge forward-only, which is what
        // fetchMore wants but not what a refetch wants — PD-2319's freshness
        // polling calls refetchQueries on any lockVersion change, and that
        // re-requests page 1 into the same field, appending a second copy of it
        // every time (PD-3743). Page 1 therefore replaces the collection; later
        // pages still append.
        merge(existing = {}, incoming, { args }) {
          const { collection: existingColection = [] } = existing;
          const { collection: incomingCollection = [] } = incoming;

          const isFirstPage = (args?.page ?? 1) === 1;

          // use incoming as default and overwrite the collection value
          return {
            ...incoming,
            collection: isFirstPage
              ? incomingCollection
              : [...existingColection, ...incomingCollection],
          };
        },
      },
      notes: {
        keyArgs: false,
        // This functionality only applies to collections with infinite scroll.
        merge(existing = {}, incoming) {
          const { collection: existingColection = [] } = existing;
          const { collection: incomingCollection = [] } = incoming;

          // use incoming as default and overwrite the collection value
          return {
            ...incoming,
            collection: [...existingColection, ...incomingCollection],
          };
        },
      },
    },
  },
  Customer: {
    fields: {
      history: {
        keyArgs: false,
        // This functionality only applies to collections with infinite scroll.
        merge(existing = {}, incoming) {
          const { collection: existingColection = [] } = existing;
          const { collection: incomingCollection = [] } = incoming;

          // use incoming as default and overwrite the collection value
          return {
            ...incoming,
            collection: [...existingColection, ...incomingCollection],
          };
        },
      },
    },
  },
  Query: {
    fields: {
      notifications: {
        keyArgs: ['filters'],
        merge(
          existing: NotificationsQuery['notifications'],
          incoming: NotificationsQuery['notifications'],
        ): NotificationsQuery['notifications'] {
          const { collection: existingColection = [] } = existing ?? {};
          const { collection: incomingCollection = [] } = incoming;

          return {
            ...incoming,
            collection: existingColection.concat(incomingCollection),
          };
        },
      },
    },
  },
  SentinelTeam: {
    fields: {
      teamMembers: {
        merge(_existing, incoming) {
          return incoming;
        },
      },
    },
  },
};
